import React, { memo } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { PROGRESS, OUTCOME } from "./useQuestionGroups";
import { a } from "../appColors";

/**
 * Every state a single part can be in.
 *
 * All of it goes through the theme tokens rather than the brand constants,
 * because a tint built for dark text on a pale ground becomes a white block
 * on a near-black card. `a.tint*` is the right tint for whichever mode is on,
 * and `a.edge` is the ink at low alpha — never a new grey.
 */
const SEGMENT = {
  todo: { bg: a.railTodo, edge: "transparent" },
  done: { bg: a.ink, edge: "transparent" },
  [OUTCOME.CORRECT]: { bg: a.railOk, edge: "transparent" },
  [OUTCOME.PARTIAL]: { bg: a.railWarn, edge: "transparent" },
  [OUTCOME.INCORRECT]: { bg: a.railBad, edge: "transparent" },
};

/**
 * One block per part of the question.
 *
 * A bar that only fills up says how much is left but not what is left; a
 * block per part says both, and after marking the same blocks carry how each
 * part went without needing a second row of anything. It shrinks to a
 * 26px rail when the list collapses, which a number-in-a-ring could not do
 * without becoming decoration.
 */
const PartsRail = memo(({ segments, compact = false }) => {
  if (!segments.length) return null;
  // A question with many parts has to fit the same rail as one with two.
  // The blocks are allowed to shrink — a floor on their width is what pushed
  // them out of the collapsed pill — and the gap closes up when crowded so
  // there is something left to see.
  const crowded = segments.length > 6;
  return (
    <Flex
      gap={crowded ? "1px" : "2px"}
      width={compact ? "26px" : "100%"}
      height="6px"
      overflow="hidden"
      aria-hidden
    >
      {segments.map((state, i) => {
        const look = SEGMENT[state] || SEGMENT.todo;
        return (
          <Box
            key={i}
            flex="1 1 0"
            minW={0}
            borderRadius={crowded ? "1px" : "3px"}
            bg={look.bg}
            border={crowded ? "none" : "1px solid"}
            borderColor={look.edge}
            transition="background 0.18s"
          />
        );
      })}
    </Flex>
  );
});
PartsRail.displayName = "PartsRail";

/**
 * The question's number.
 *
 * It inverts to stande-black once every part has an answer, which is the one
 * thing a student scanning the list wants to know. The rail below carries the
 * detail, so this stays a single clean signal rather than a second gauge.
 */
const NumberTile = memo(({ number, progress }) => {
  const complete = progress === PROGRESS.COMPLETE;
  return (
    <Flex
      align="center"
      justify="center"
      boxSize="22px"
      flexShrink={0}
      borderRadius="6px"
      bg={complete ? a.ink : a.surfaceSubtle}
      color={complete ? a.onInk : a.text}
      border="1px solid"
      borderColor={complete ? a.ink : a.edge}
      fontSize="10px"
      fontWeight="600"
      lineHeight="1"
      transition="background 0.18s, color 0.18s"
    >
      {number}
    </Flex>
  );
});
NumberTile.displayName = "NumberTile";

const WORDS = [
  { key: "correct", word: "correct", bg: a.chipOkBg, edge: a.chipOkEdge, fg: a.okText },
  { key: "partial", word: "partly", bg: a.chipWarnBg, edge: a.chipWarnEdge, fg: a.warnText },
  { key: "incorrect", word: "missed", bg: a.chipBadBg, edge: a.chipBadEdge, fg: a.badText },
];

/**
 * What the marker made of the question, in words.
 *
 * Bare numbers beside coloured squares do not say what any of them mean, so
 * each count carries its word. The tints are the brand's, and they are used
 * the way tints are meant to be — as a ground under black text, not as ink.
 */
const OutcomeWords = memo(({ tally }) => {
  const shown = WORDS.filter((w) => tally[w.key] > 0);
  if (!shown.length) return null;
  return (
    <Flex gap="4px" flexWrap="wrap" align="center">
      {shown.map((w) => (
        <Text
          key={w.key}
          as="span"
          px="6px"
          py="1px"
          borderRadius="200px"
          bg={w.bg}
          border="1px solid"
          borderColor={w.edge}
          color={w.fg}
          fontSize="9px"
          lineHeight="14px"
          whiteSpace="nowrap"
        >
          {tally[w.key]} {w.word}
        </Text>
      ))}
    </Flex>
  );
});
OutcomeWords.displayName = "OutcomeWords";

/** The plain-words version, for tooltips and screen readers. */
export const statusSentence = (group) => {
  if (group.isMarked) {
    const parts = WORDS.filter((w) => group.tally[w.key] > 0).map(
      (w) => `${group.tally[w.key]} ${w.word}`
    );
    return parts.join(", ");
  }
  if (group.answerableCount === 0) return "Nothing to answer";
  if (group.answeredCount === 0) return "Not started";
  if (group.progress === PROGRESS.COMPLETE) return `All ${group.answerableCount} parts answered`;
  return `${group.answeredCount} of ${group.answerableCount} parts answered`;
};

export { PartsRail, NumberTile, OutcomeWords };
